import { useEffect, useState } from 'react';
import { envoisApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { dateSuisse } from '../../lib/dates';
import { C } from '../../theme/palette';
import { tr } from '../../lib/i18n';

/** Les messages que le patron réécrit, rangés par sorte. */
const RAPPELS = [
  ['rappel1', '1er rappel', 1],
  ['rappel2', '2e rappel', 2],
  ['rappel3', 'Dernier rappel', 3],
];

/** Les mots remplacés tout seuls, selon la sorte de message. */
const MOTS = {
  facture: [
    'client',
    'numero',
    'date',
    'montant',
    'echeance',
    'retard',
    'frais',
    'solde',
    'societe',
  ],
  offre: ['client', 'numero', 'date', 'montant', 'echeance', 'societe'],
};

const champ = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid ' + C.border,
  borderRadius: 9,
  padding: '9px 10px',
  fontSize: 11,
  fontFamily: 'inherit',
  lineHeight: 1.45,
  background: '#fff',
};

const etiquette = {
  fontSize: 8.5,
  fontWeight: 800,
  color: C.muted,
  textTransform: 'uppercase',
  margin: '9px 0 4px',
};

const jourDecale = (jours) => {
  const d = new Date();
  d.setDate(d.getDate() + jours);
  return dateSuisse(d.toISOString().slice(0, 10));
};

/** Remplace les mots entre accolades ; sans client, « Bonjour {client}, » devient « Bonjour, ». */
export function remplit(modele, valeurs) {
  let texte = modele || '';
  if (!valeurs.client) texte = texte.split(' {client}').join('').split('{client}').join('');
  for (const [mot, valeur] of Object.entries(valeurs))
    texte = texte.split('{' + mot + '}').join(valeur ?? '');
  return texte;
}

/**
 * Les textes des rappels de facture (1er, 2e, dernier) et de la relance d'une
 * offre sans réponse, réunis à un seul endroit. Le patron réécrit l'objet et
 * le texte de chacun ; « Texte standard » remet le texte proposé par
 * SwissTech. Le même texte part avec l'envoi automatique et à la main.
 *
 * @param {{ soc: object }} props la société, pour l'aperçu
 * @returns {JSX.Element|null} la carte
 */
export function TextesRelancesCard({ soc }) {
  const { session } = useSession();
  const peutModifier = session?.role === 'patron';
  const [reglages, setReglages] = useState(null);
  const [textes, setTextes] = useState({});
  const [sorte, setSorte] = useState('facture');
  const [rappel, setRappel] = useState('rappel1');
  const [apercu, setApercu] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let annule = false;
    envoisApi
      .reglages()
      .then((r) => {
        if (annule || !r?.textesStandard) return;
        const std = r.textesStandard;
        const ecrits = r.textes || {};
        const t = {};
        for (const cle of Object.keys(std)) {
          t[cle] = {
            objet: ecrits[cle]?.objet ?? std[cle]?.objet ?? '',
            texte: ecrits[cle]?.texte ?? std[cle]?.texte ?? '',
          };
        }
        setReglages(r);
        setTextes(t);
      })
      .catch(() => {
        if (!annule) setErreur('Les textes n’ont pas pu être lus.');
      });
    return () => {
      annule = true;
    };
  }, []);

  if (!reglages) {
    return erreur ? (
      <Card style={{ marginBottom: 12 }}>
        <div style={{ padding: 13, fontSize: 10, color: C.red, fontWeight: 700 }}>{erreur}</div>
      </Card>
    ) : null;
  }

  const std = reglages.textesStandard;
  const cle = sorte === 'offre' ? 'offre' : rappel;
  const courant = textes[cle] || { objet: '', texte: '' };
  const estStandard =
    courant.objet === (std[cle]?.objet ?? '') && courant.texte === (std[cle]?.texte ?? '');
  const change = (patch) => {
    setConfirmation('');
    setTextes((p) => ({ ...p, [cle]: { ...courant, ...patch } }));
  };

  // L'aperçu : le message avec un client et des montants d'exemple.
  const niveau = RAPPELS.find(([c]) => c === cle)?.[2] || 1;
  const frais = Number(reglages['rappelFrais' + niveau]) || 0;
  const retard = Number(reglages['rappelJours' + niveau]) || 10;
  const valeurs = {
    client: 'Famille Exemple',
    numero: '2026-042',
    date: jourDecale(sorte === 'offre' ? -15 : -30 - retard),
    montant: DEVISE + ' ' + fmt(1250),
    echeance: jourDecale(sorte === 'offre' ? 15 : -retard),
    retard: String(retard),
    frais: DEVISE + ' ' + fmt(frais),
    solde: DEVISE + ' ' + fmt(1250 + frais),
    societe: soc?.nom || '',
  };

  const enregistre = async () => {
    setEnCours(true);
    setErreur('');
    setConfirmation('');
    try {
      // Les autres réglages sont relus juste avant : la carte du dessus a pu
      // les changer depuis l'ouverture de l'écran.
      const actuels = await envoisApi.reglages();
      const aEnvoyer = {};
      for (const [c, t] of Object.entries(textes)) {
        aEnvoyer[c] = {
          objet: t.objet === std[c]?.objet ? '' : t.objet,
          texte: t.texte === std[c]?.texte ? '' : t.texte,
        };
      }
      // eslint-disable-next-line no-unused-vars
      const { relanceDevisTexte, textesStandard, ...reste } = actuels || {};
      await envoisApi.enregistreReglages({ ...reste, textes: aEnvoyer });
      setConfirmation(tr('Textes enregistrés.'));
    } catch (e) {
      setErreur(e?.message || 'L’enregistrement a échoué.');
    } finally {
      setEnCours(false);
    }
  };

  const pastille = (on, coul) => ({
    flex: 1,
    textAlign: 'center',
    padding: '8px 4px',
    borderRadius: 9,
    fontSize: 10,
    fontWeight: 800,
    cursor: 'pointer',
    border: '1.5px solid ' + (on ? coul : C.border),
    background: on ? coul : '#fff',
    color: on ? '#fff' : C.grey,
  });

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="✍️"
        title="Textes des rappels et relances"
        color={C.teal}
        sub="Le texte que reçoit le client · modifiable quand vous voulez"
      />
      <div style={{ padding: 13 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          {[
            ['facture', '🧾 ' + tr('Facture non payée')],
            ['offre', '📄 ' + tr('Offre sans réponse')],
          ].map(([s, lb]) => (
            <div
              key={s}
              role="tab"
              aria-selected={sorte === s}
              onClick={() => {
                setSorte(s);
                setApercu(false);
              }}
              style={pastille(sorte === s, C.navy)}
            >
              {lb}
            </div>
          ))}
        </div>
        {sorte === 'facture' && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {RAPPELS.map(([c, lb]) => (
              <div
                key={c}
                role="tab"
                aria-selected={rappel === c}
                onClick={() => {
                  setRappel(c);
                  setApercu(false);
                }}
                style={pastille(rappel === c, C.red)}
              >
                {tr(lb)}
              </div>
            ))}
          </div>
        )}

        <span
          style={{
            display: 'inline-block',
            fontSize: 8.5,
            fontWeight: 800,
            borderRadius: 6,
            padding: '2px 7px',
            color: estStandard ? C.teal : C.amber,
            background: (estStandard ? C.teal : C.amber) + '18',
          }}
        >
          {estStandard ? tr('Texte standard SwissTech') : tr('Votre texte')}
        </span>

        <div style={etiquette}>{tr('Objet de l’e-mail')}</div>
        <input
          aria-label="Objet de l’e-mail"
          value={courant.objet}
          disabled={!peutModifier}
          onChange={(e) => change({ objet: e.target.value })}
          style={champ}
        />
        <div style={etiquette}>{tr('Texte')}</div>
        <textarea
          aria-label="Texte du message"
          rows={16}
          value={courant.texte}
          disabled={!peutModifier}
          onChange={(e) => change({ texte: e.target.value })}
          style={champ}
        />
        <div style={{ fontSize: 8.5, color: C.muted, marginTop: 5, lineHeight: 1.8 }}>
          {tr('Remplacés tout seuls :')}{' '}
          {MOTS[sorte].map((m) => (
            <span
              key={m}
              style={{
                background: C.navyXL,
                borderRadius: 5,
                padding: '1px 5px',
                marginRight: 3,
                color: C.ink,
                whiteSpace: 'nowrap',
              }}
            >
              {'{' + m + '}'}
            </span>
          ))}
        </div>
        <div style={{ fontSize: 8.5, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>
          {sorte === 'offre'
            ? tr('Le PDF de l’offre et le bouton « Voir, signer ou refuser » suivent toujours.')
            : tr('Le rappel en PDF, avec son bulletin de paiement, suit toujours.')}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          {peutModifier && (
            <div style={{ flex: 1 }}>
              <Btn
                label={'↺ ' + tr('Texte standard')}
                ghost
                color={C.navy}
                full
                disabled={estStandard}
                onClick={() =>
                  change({ objet: std[cle]?.objet ?? '', texte: std[cle]?.texte ?? '' })
                }
              />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <Btn
              label={'👁 ' + tr('Aperçu')}
              color={C.teal}
              full
              onClick={() => setApercu(!apercu)}
            />
          </div>
        </div>

        {apercu && (
          <div
            data-testid="apercu-texte-relance"
            style={{
              marginTop: 8,
              background: C.navyXL,
              border: '1px solid ' + C.border,
              borderRadius: 9,
              padding: '9px 11px',
              fontSize: 10,
              lineHeight: 1.5,
            }}
          >
            <div style={{ fontWeight: 800 }}>
              {tr('Objet :')} {remplit(courant.objet, valeurs)}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: 5 }}>
              {remplit(courant.texte, valeurs)}
            </div>
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          {peutModifier ? (
            <Btn
              label={enCours ? '⏳ Enregistrement…' : '✓ ' + tr('Enregistrer les textes')}
              color={C.navy}
              full
              disabled={enCours}
              onClick={enregistre}
            />
          ) : (
            <div style={{ fontSize: 8.5, color: C.muted }}>
              Seul le patron modifie ces réglages.
            </div>
          )}
        </div>
        {confirmation && (
          <div
            style={{ marginTop: 8, fontSize: 10, color: C.green, fontWeight: 700 }}
            role="status"
          >
            ✓ {confirmation}
          </div>
        )}
        {erreur && (
          <div style={{ marginTop: 8, fontSize: 10, color: C.red, fontWeight: 700 }}>{erreur}</div>
        )}
      </div>
    </Card>
  );
}
